import {
  NOTIFICATION_CHANNEL_KEYS,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_GROUP_KEYS,
  NOTIFICATION_GROUP_MODULE,
  NOTIFICATION_GROUPS,
  type NotificationChannel,
  type NotificationGroup,
  type NotificationSettings,
  type NotificationSettingsDto,
} from '@bqcrm/shared';
import { Alert, Button, Checkbox, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useMe } from '../auth';
import { notifyError, notifyOk } from '../notify';
import { QueryState } from './QueryState';

/**
 * Ustawienia powiadomień zalogowanej osoby (docs/16): dla każdej grupy — dzwonek w panelu, aplikacja mobilna,
 * e-mail z podsumowaniem dnia. Każdy ustawia tylko swoje.
 */
export function NotificationSettingsModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const phone = useMediaQuery('(max-width: 48em)');
  const me = useMe().data;
  // grupy modułów opcjonalnych wyłączonych w systemie — niewidoczne (jakby modułu nie było)
  const groups = NOTIFICATION_GROUP_KEYS.filter((g) => { const m = NOTIFICATION_GROUP_MODULE[g]; return !m || !!me?.features[m]; });
  const q = useQuery({ queryKey: ['notification-settings'], queryFn: () => api<NotificationSettingsDto>('/me/notification-settings'), enabled: opened });
  const [s, setS] = useState<NotificationSettings | null>(null);
  useEffect(() => {
    if (q.data) setS(q.data.settings);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (settings: NotificationSettings) => api<NotificationSettingsDto>('/me/notification-settings', { method: 'PUT', body: settings }),
    onSuccess: (d) => {
      qc.setQueryData(['notification-settings'], d);
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      notifyOk('Zapisano ustawienia powiadomień');
      onClose();
    },
    onError: notifyError,
  });
  const set = (g: NotificationGroup, c: NotificationChannel, v: boolean) => setS((p) => (p ? { ...p, [g]: { ...p[g], [c]: v } } : p));
  const noEmail = !q.data?.email;

  return (
    <Modal opened={opened} onClose={onClose} title="Powiadomienia" size="xl" fullScreen={phone}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {s && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Wybierz, gdzie chcesz dostawać powiadomienia z każdej grupy. Przeczytanie w jednym miejscu (panel albo telefon) oznacza powiadomienie jako przeczytane wszędzie.
            </Text>
            <Table.ScrollContainer minWidth={560}>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Grupa</Table.Th>
                    {NOTIFICATION_CHANNEL_KEYS.map((c) => (
                      <Table.Th key={c} ta="center" w={130}>
                        {NOTIFICATION_CHANNELS[c]}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groups.map((g) => (
                    <Table.Tr key={g}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {NOTIFICATION_GROUPS[g].label}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {NOTIFICATION_GROUPS[g].hint}
                        </Text>
                      </Table.Td>
                      {NOTIFICATION_CHANNEL_KEYS.map((c) => (
                        <Table.Td key={c}>
                          <Group justify="center">
                            <Checkbox
                              aria-label={`${NOTIFICATION_GROUPS[g].label}: ${NOTIFICATION_CHANNELS[c]}`}
                              checked={s[g][c]}
                              disabled={c === 'email' && noEmail}
                              onChange={(e) => set(g, c, e.currentTarget.checked)}
                            />
                          </Group>
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Alert color="gray" variant="light">
              <Text size="xs">
                <b>Aplikacja mobilna:</b> {q.data?.pushAvailable ? 'powiadomienia przychodzą od razu na telefon.' : 'powiadomienia pojawiają się w dzwonku aplikacji po synchronizacji (powiadomienia „push” dojdą później).'}
                <br />
                <b>E-mail:</b> {noEmail ? 'konto nie ma adresu e-mail.' : `raz dziennie rano, na ${q.data?.email}, nieprzeczytane powiadomienia z ostatniej doby z zaznaczonych grup.`}
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Anuluj
              </Button>
              <Button onClick={() => save.mutate(s)} loading={save.isPending}>
                Zapisz
              </Button>
            </Group>
          </Stack>
        )}
      </QueryState>
    </Modal>
  );
}
